/**
 * Announce a new restaurant-app BUILD to tablets.
 *
 * Only for changes an over-the-air update cannot carry — native code, a new
 * plugin, an SDK bump — which need a real APK install. A tablet on an older
 * build is told nothing by EAS: it asks for updates, is correctly told there are
 * none for its runtime version, and would sit on the old version indefinitely.
 *
 * Setting a version here puts a "tap to download" bar on every tablet running
 * something older. Ordinary JavaScript releases need nothing here — those reach
 * tablets on their own.
 */
import React, { useState } from 'react'
import { useMutation, gql } from '@apollo/client'
import {
  Box, Typography, Button, TextField, Alert, CircularProgress
} from '@mui/material'
import SystemUpdateIcon from '@mui/icons-material/SystemUpdate'
import { saveRestaurantAppReleaseConfiguration } from '../../../apollo'

const SAVE = gql`${saveRestaurantAppReleaseConfiguration}`

const labelSx = { fontSize: '0.78rem', fontWeight: 700, color: '#374151', mb: 0.6, textTransform: 'uppercase', letterSpacing: '0.05em' }
const fieldSx = { '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '0.875rem' } }

function RestaurantAppRelease(props) {
  const [version, setVersion] = useState(props.restaurantAppLatestVersion || '')
  const [url, setUrl] = useState(props.restaurantAppDownloadUrl || '')
  const [success, setSuccess] = useState('')
  const [error, setError] = useState('')

  const [mutate, { loading }] = useMutation(SAVE, {
    onCompleted: () => {
      setSuccess('Saved. Tablets on an older build will show the download bar.')
      setError('')
      setTimeout(() => setSuccess(''), 4000)
    },
    onError: (e) => {
      setError(e.message || 'Failed to save.')
      setSuccess('')
      setTimeout(() => setError(''), 4000)
    }
  })

  const handleSave = () => {
    const v = version.trim()
    const u = url.trim()
    // Both or neither. A version with no link leaves staff told to update with
    // nowhere to go, and a link with no version is never shown to anyone.
    if ((v && !u) || (!v && u)) {
      setError('Set both the version and the download link, or clear both.')
      setTimeout(() => setError(''), 4000)
      return
    }
    if (v && !/^\d+(\.\d+)*$/.test(v)) {
      setError('Version must be numbers and dots, e.g. 1.1.0')
      setTimeout(() => setError(''), 4000)
      return
    }
    mutate({
      variables: {
        configurationInput: {
          restaurantAppLatestVersion: v,
          restaurantAppDownloadUrl: u
        }
      }
    })
  }

  return (
    <Box sx={{ borderRadius: '16px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' }}>
      <Box sx={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e3a8a 60%, #1d4ed8 100%)', px: 2.5, py: 2, display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Box sx={{ width: 36, height: 36, borderRadius: '10px', bgcolor: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <SystemUpdateIcon sx={{ color: '#93c5fd', fontSize: 18 }} />
        </Box>
        <Typography sx={{ fontWeight: 700, fontSize: '0.95rem', color: '#fff' }}>Restaurant App Release</Typography>
      </Box>

      <Box sx={{ p: 2.5, bgcolor: '#fff' }}>
        <Typography sx={{ fontSize: '0.78rem', color: '#64748b', mb: 2 }}>
          Only needed when a new APK must be installed by hand. Normal updates
          reach tablets on their own — leave this alone for those.
        </Typography>

        <Box sx={{ mb: 2 }}>
          <Typography sx={labelSx}>Latest version</Typography>
          <TextField
            fullWidth size="small" placeholder="1.1.0" sx={fieldSx}
            value={version} onChange={(e) => setVersion(e.target.value)}
          />
        </Box>

        <Box sx={{ mb: 2 }}>
          <Typography sx={labelSx}>Download link</Typography>
          <TextField
            fullWidth size="small" placeholder="https://expo.dev/artifacts/eas/....apk" sx={fieldSx}
            value={url} onChange={(e) => setUrl(e.target.value)}
          />
        </Box>

        {success && <Alert severity="success" sx={{ mb: 2, borderRadius: '10px' }}>{success}</Alert>}
        {error && <Alert severity="error" sx={{ mb: 2, borderRadius: '10px' }}>{error}</Alert>}

        <Button
          fullWidth variant="contained" onClick={handleSave} disabled={loading}
          sx={{ borderRadius: '10px', textTransform: 'none', fontWeight: 700, bgcolor: '#1d4ed8', '&:hover': { bgcolor: '#1e40af' } }}
        >
          {loading ? <CircularProgress size={20} sx={{ color: '#fff' }} /> : 'Save'}
        </Button>
      </Box>
    </Box>
  )
}

export default RestaurantAppRelease
